/**
 * Adding a site to watch, and editing one.
 *
 * ## What the address field shows while typing
 *
 * The address that will be checked, not the one typed: "Firma.ee/pood?utm=x"
 * is saved as "https://firma.ee/pood", and the operator should see that before
 * pressing Add (`normalizeMonitorUrl`, the browser copy of the API's rule). A
 * paste the server would refuse - an IP address, a name without a dot, a port -
 * says why under the field and keeps the button off.
 *
 * ## Editing cannot change the site or the client
 *
 * PATCH takes the label, the package, the project and the pause flag only. A
 * different address is a different site with a different history, and the
 * uptime of the old one must not be carried over to it; a different client is
 * a different contract. Both show read-only when editing, with a line saying
 * how to change them.
 *
 * ## A site on our own platform
 *
 * The first field offers the platform's own sites nobody watches yet (item K,
 * `platform/PlatformSiteField.tsx`). Picking one takes the address from that
 * site's verified domains (a list when it has several, the primary first)
 * instead of a typed one, and picks the client whose website is on it when the
 * register has one. The client stays required: for one of our own sites the
 * operator picks our own firm. The package's WordPress-only lines are left out
 * of the hint for such a site.
 *
 * ## The package
 *
 * The web maintenance packages come from the package editor
 * (docs/saidiseire-paketid.md), so a package made there is listed here at once.
 * The line under the field says what the picked package (or no package) makes
 * the engine do. A site whose package was deleted keeps its code as an option,
 * so opening the dialog does not quietly move it to no package.
 *
 * ## The project
 *
 * The client's project the site's maintenance is logged in. The monthly
 * report's "work done" lists that project's time entries and nothing else, so
 * a site with no project gets no work section at all. Open projects of the
 * picked client only (the server refuses any other); a current project that is
 * not in the list (archived, or the list could not be read) stays as an
 * option, so saving the label does not quietly unlink it.
 *
 * ## After adding
 *
 * The server runs the first check before it answers. When the site answered,
 * the dialog closes with a toast. When it did not - refused on https, a firewall,
 * a certificate - the dialog stays open and says so, with the next step for
 * that refusal (`adviceKey`), because a site that has never been seen working
 * sends no alerts until it is (the engine will not page anybody about a site it
 * has no evidence ever worked), and the operator adding it is the one person
 * who can fix the address right now. When the check could not run at all (our
 * own network was down, say), the site is saved and the dialog says the engine
 * will look on its next round, rather than calling the site unknown.
 */
import { useState, type FormEvent } from "react";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { useI18n } from "../../../i18n/I18nProvider";
import {
  MONITOR_LABEL_MAX,
  adviceKey,
  createMonitor,
  monitorErrorKey,
  normalizeMonitorUrl,
  planGone,
  planName,
  policyIncludes,
  updateMonitor,
  type CreateMonitorResult,
  type MonitorRow,
} from "../../../lib/monitoring";
import { platformPolicyLines, unwatched, type PlatformSiteOption } from "../../../lib/monitoringPlatform";
import { Btn, Modal, SelectInput, useToast } from "../../ui";
import { ClientPicker } from "../_shared/ClientPicker";
import { Field, Pill } from "../_shared/formBits";
import { PlatformSiteField } from "./platform";
import { useClientProjects } from "./useClientProjects";
import { useMonitorPlans } from "./useMonitorPlans";
import { codeText, type T } from "./words";

export function MonitorDialog({
  site,
  platformSites = null,
  preset = null,
  t,
  onClose,
  onSaved,
  onOpen,
}: {
  /** The site being edited, or null to add one. */
  site: MonitorRow | null;
  /** The platform's own sites, to pick from when adding; null when they are not offered. */
  platformSites?: readonly PlatformSiteOption[] | null;
  /** A platform site the dialog opens with already picked. */
  preset?: PlatformSiteOption | null;
  t: T;
  onClose: () => void;
  onSaved: (site: MonitorRow) => void;
  /** Goes to a site's page - offered after an add whose first check failed. */
  onOpen: (id: string) => void;
}) {
  const toast = useToast();
  const { locale } = useI18n();
  const catalog = useMonitorPlans();
  const editing = site !== null;
  const [platform, setPlatform] = useState<PlatformSiteOption | null>(editing ? null : preset);
  const [clientName, setClientName] = useState(site?.clientName ?? preset?.client?.name ?? "");
  const [clientId, setClientId] = useState<string | null>(site?.clientId ?? preset?.client?.id ?? null);
  const [url, setUrl] = useState(site?.url ?? preset?.host ?? "");
  const [label, setLabel] = useState(site?.label ?? "");
  const [plan, setPlan] = useState<string>(site?.planCode ?? "");
  const [project, setProject] = useState<string>(site?.projectId ?? "");
  const projects = useClientProjects(editing ? site.clientId : clientId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<CreateMonitorResult | null>(null);

  const preview = normalizeMonitorUrl(url);
  const canSave = editing || (Boolean(clientId) && preview.ok);
  // The picked site stays in the list even once somebody else started watching it, so the field never shows a blank.
  const offered = editing ? [] : unwatched(platformSites ?? []);
  const platformOptions = platform && !offered.some((s) => s.siteId === platform.siteId) ? [platform, ...offered] : offered;
  const onPlatform = editing ? Boolean(site.platformSiteId) : platform !== null;

  function pickPlatform(next: PlatformSiteOption | null) {
    setPlatform(next);
    // Back to a typed address: the site's own one must not stay behind as if somebody typed it.
    setUrl(next?.host ?? "");
    if (next?.client && !clientId) {
      setClientId(next.client.id);
      setClientName(next.client.name);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSave || busy) return;
    setBusy(true);
    setError(null);
    const planCode = plan || null;
    const projectId = project || null;
    const cleanLabel = label.trim().slice(0, MONITOR_LABEL_MAX) || null;
    try {
      if (editing) {
        const res = await updateMonitor(site.id, { label: cleanLabel, planCode, projectId });
        onSaved(res.site);
        toast(t("monitoring.toast.saved"));
        onClose();
        return;
      }
      const res = await createMonitor({
        clientId: clientId!,
        url: preview.ok ? preview.url : url,
        label: cleanLabel,
        planCode,
        projectId,
        ...(platform ? { platformSiteId: platform.siteId } : {}),
      });
      onSaved(res.site);
      if (res.site.state === "up" && res.alsoWatchedBy.length === 0) {
        toast(t("monitoring.toast.addedUp", { host: res.site.host }));
        onClose();
        return;
      }
      setAdded(res);
    } catch (err) {
      setError(t(`monitoring.error.${monitorErrorKey(err)}`));
    } finally {
      setBusy(false);
    }
  }

  if (added) {
    const first = added.site;
    const up = first.state === "up";
    const ran = first.lastCheckAt !== null;
    const advice = adviceKey(first);
    const heading = t("monitoring.dialog.addedTitle");
    return (
      <Modal title={heading} label={heading} onClose={onClose} className="max-w-[480px]">
        <div className="flex items-start gap-3 pt-1">
          {up ? (
            <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-[var(--color-success)]" />
          ) : (
            <AlertTriangle className="mt-0.5 size-5 shrink-0 text-[var(--color-warning)]" />
          )}
          <div className="min-w-0 space-y-2">
            <p className="text-[13.5px] font-semibold">{first.host}</p>
            <p className="crm-sub">
              {up
                ? t("monitoring.dialog.firstUp")
                : !ran
                  ? t("monitoring.dialog.firstNotRun")
                  : t("monitoring.dialog.firstOther", {
                      what: codeText(t, first.lastCode) ?? t(`monitoring.state.${first.state}`),
                    })}
            </p>
            {/* The never-seen advice already says that nothing is sent and
                what to check; every other refusal gets its own next step, then
                the rule that no alert goes out before the site has worked. */}
            {ran && advice ? <p className="crm-sub">{t(`monitoring.advice.${advice}`)}</p> : null}
            {!up && ran && advice !== "neverUp" ? <p className="crm-sub">{t("monitoring.dialog.noAlertsYet")}</p> : null}
            {added.alsoWatchedBy.length > 0 ? (
              <p className="crm-sub">
                {t("monitoring.dialog.alsoWatched", {
                  names: added.alsoWatchedBy.map((c) => c.clientName).join(", "),
                })}
              </p>
            ) : null}
          </div>
        </div>
        <div className="crm-m-foot">
          <Btn onClick={() => onOpen(first.id)}>{t("monitoring.menu.open")}</Btn>
          <Btn tone="dark" onClick={onClose}>
            {t("common.close")}
          </Btn>
        </div>
      </Modal>
    );
  }

  const heading = editing ? t("monitoring.dialog.editTitle") : t("monitoring.dialog.addTitle");
  const plans = catalog?.plans ?? [];
  const picked = plans.find((p) => p.code === plan) ?? null;
  // What the engine will do for the site: the picked package's rules, or the defaults.
  const rules = picked ? picked.policy : plan ? null : (catalog?.defaults ?? null);
  const rulesText = rules
    ? t(picked ? "monitoring.dialog.planHint" : "monitoring.dialog.planHintNone", {
        list: (onPlatform ? platformPolicyLines(policyIncludes(rules)) : policyIncludes(rules))
          .map((line) => t(`monitoring.policy.${line.key}`, line.vars))
          .join(", "),
      })
    : undefined;

  return (
    <Modal title={heading} label={heading} onClose={onClose} className="max-w-[520px]">
      <form onSubmit={(e) => void submit(e)} autoComplete="off">
        {!editing ? <PlatformSiteField sites={platformOptions} value={platform?.siteId ?? ""} onPick={pickPlatform} t={t} /> : null}

        <Field
          label={t("monitoring.dialog.client")}
          hint={!editing && platform && !clientId ? t("monitoring.platform.field.clientHint") : undefined}
        >
          {editing ? (
            <div className="crm-inp opacity-70">{site.clientName}</div>
          ) : (
            <ClientPicker
              value={clientName}
              selectedId={clientId}
              onChange={(v) => {
                setClientName(v);
                // Typing again means the earlier pick no longer stands, and neither does its project.
                setClientId(null);
                setProject("");
              }}
              onPick={(id, name) => {
                setClientId(id);
                setClientName(name);
                setProject("");
              }}
              placeholder={t("monitoring.dialog.clientPlaceholder")}
              required
            />
          )}
        </Field>

        <Field
          label={t("monitoring.dialog.url")}
          hint={
            editing ? (
              t("monitoring.dialog.urlLocked")
            ) : platform ? (
              t("monitoring.platform.field.addressHint")
            ) : url.trim() === "" ? (
              t("monitoring.dialog.urlHint")
            ) : preview.ok ? (
              <>
                {t("monitoring.dialog.willCheck")} <b className="crm-tnum">{preview.url}</b>
              </>
            ) : (
              <span className="text-[var(--color-destructive)]">{t(`monitoring.url.${preview.problem}`)}</span>
            )
          }
        >
          {editing ? (
            <div className="crm-inp crm-trunc opacity-70">{site.url}</div>
          ) : platform && platform.hosts.length > 1 ? (
            <SelectInput value={url} onChange={setUrl} label={t("monitoring.dialog.url")}>
              {platform.hosts.map((host) => (
                <option key={host} value={host}>
                  {host}
                </option>
              ))}
            </SelectInput>
          ) : platform ? (
            <div className="crm-inp crm-trunc opacity-70">{platform.host}</div>
          ) : (
            <Pill value={url} onChange={setUrl} placeholder={t("common.example.domain")} inputMode="url" />
          )}
        </Field>

        <Field label={t("monitoring.dialog.label")} hint={t("monitoring.dialog.labelHint")}>
          <Pill value={label} onChange={(v) => setLabel(v.slice(0, MONITOR_LABEL_MAX))} placeholder={site?.host ?? ""} />
        </Field>

        <Field label={t("monitoring.dialog.plan")} hint={rulesText}>
          <SelectInput value={plan} onChange={setPlan} label={t("monitoring.dialog.plan")}>
            <option value="">{t("monitoring.dialog.planNone")}</option>
            {plans.map((p) => (
              <option key={p.code} value={p.code}>
                {p.isPublic ? planName(p, locale) : t("monitoring.policy.hidden", { name: planName(p, locale) })}
              </option>
            ))}
            {/* Not in the list: a deleted package (kept so the dialog does not move the site
                off it), or the list could not be read (kept so saving the label does not either). */}
            {plan && !plans.some((p) => p.code === plan) ? (
              <option value={plan}>
                {planGone(catalog?.plans ?? null, plan) ? t("monitoring.policy.goneOption", { code: plan }) : plan}
              </option>
            ) : null}
          </SelectInput>
        </Field>

        {editing || clientId ? (
          <Field label={t("monitoring.dialog.project")} hint={t("monitoring.dialog.projectHint")}>
            <SelectInput value={project} onChange={setProject} label={t("monitoring.dialog.project")}>
              <option value="">{t("monitoring.dialog.projectNone")}</option>
              {(projects ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
              {/* Not in the list (archived, or the list could not be read): kept, so saving does not unlink it. */}
              {project && !(projects ?? []).some((p) => p.id === project) ? (
                <option value={project}>{t("monitoring.dialog.projectOther")}</option>
              ) : null}
            </SelectInput>
          </Field>
        ) : null}

        {error ? (
          <p className="mt-3 text-[13px] text-[var(--color-destructive)]" role="alert">
            {error}
          </p>
        ) : null}

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="dark" disabled={busy || !canSave}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            {editing ? t("common.save") : busy ? t("monitoring.dialog.checking") : t("monitoring.dialog.add")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
